import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import APIUtil from 'util/APIUtil';

import {ProcessFlowchartEdgeType, ProcessFlowchartNodeType} from './processFlowchartConfigEditorConstants';
import {TaskSchemaStaticNodeData} from './processFlowchartConfigEditorNodeTypes';

export const NODE_IDENTIFIER_PREFIX = 'node';
export const FIELD_IDENTIFIER_PREFIX = 'field';

export function generateTaskSchemaNodeIdentifier(taskSchema: {id: string}): string {
  const {id} = taskSchema;
  // Task schemas can be repeated in a flowchart, add a unique identifier to distinguish nodes
  const hash = uuidv4();
  return `${NODE_IDENTIFIER_PREFIX}:${id}:${hash}`;
}

export function generateLogicNodeIdentifier(nodeType: ProcessFlowchartNodeType): string {
  const hash = uuidv4();
  return `${NODE_IDENTIFIER_PREFIX}:${nodeType}:${hash}`;
}

export function getSchemaFieldInfoFromHandle(handleId: string): {
  fieldId: string;
  sectionName: string;
} {
  const identifierParts = handleId.split(':');
  assert(identifierParts.length === 3, `Invalid handleId: ${handleId}`);
  const fieldContents = identifierParts[2];
  const fieldId = fieldContents.split('-')[0];
  const sectionName = identifierParts[1];
  assert(APIUtil.isSchemaField(fieldId), `Invalid fieldId: ${fieldId}`);
  return {fieldId, sectionName};
}

export function getSchemaFieldIdFromRowIdentifier(rowIdentifier: string): string {
  const identifierParts = rowIdentifier.split(':');
  assert(identifierParts.length === 3, `Invalid rowIdentifier: ${rowIdentifier}`);
  const fieldId = identifierParts[2];
  assert(APIUtil.isSchemaField(fieldId), `Invalid fieldId: ${fieldId}`);
  return fieldId;
}

export function getSchemaFieldClientIdFromHandleAndNode(
  rowHandleId: string,
  node: TaskSchemaStaticNodeData
): string {
  const {fieldId, sectionName} = getSchemaFieldInfoFromHandle(rowHandleId);
  const section = node.sections.find((section) => section.sectionName === sectionName);
  assert(section != null, `Section ${sectionName} not found in node ${node.id}`);

  const row = node.sections
    .find((section) => section.sectionName === sectionName)
    ?.sectionRows.find((row) => getSchemaFieldIdFromRowIdentifier(row.id) === fieldId);
  assert(row != null, `Row ${rowHandleId} not found in section ${sectionName} of node ${node.id}`);
  return row.clientId;
}

export function generateTaskSchemaFieldRowIdentifier(fieldId: string, sectionName: string): string {
  return `${FIELD_IDENTIFIER_PREFIX}:${sectionName}:${fieldId}`;
}

export function makeDataFlowEdgeHandle(
  fieldId: string,
  taskSchemaNodeSection: string,
  handleType: 'source' | 'target'
): string {
  return `${generateTaskSchemaFieldRowIdentifier(fieldId, taskSchemaNodeSection)}-${handleType}`;
}

export function doesDataFlowEdgeHandleReferenceRow(
  dataFlowEdgeHandle: string,
  rowIdentifier: string
): boolean {
  return (
    getSchemaFieldIdFromRowIdentifier(rowIdentifier) ===
    getSchemaFieldInfoFromHandle(dataFlowEdgeHandle).fieldId
  );
}

export function getEdgeTypeFromHandleId(handleId: string): ProcessFlowchartEdgeType {
  if (handleId.startsWith(FIELD_IDENTIFIER_PREFIX)) {
    return ProcessFlowchartEdgeType.DATA_FLOW;
  }
  return ProcessFlowchartEdgeType.CONTROL_FLOW;
}
